import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, AlertTriangle, Clock, ShieldAlert, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../config';

function PoliceReportInput({ claim, onSaved, onCancel }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/claims/${encodeURIComponent(claim.report_no)}`, {
        police_report_no: value.trim(),
        police_report_not_ready: false,
      });
      toast.success(`Police report # saved for ${claim.report_no}`);
      window.dispatchEvent(new Event('todo-refresh'));
      onSaved();
    } catch {
      toast.error('Failed to save police report #');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 py-2 bg-blue-50/80 border-b border-gray-100" data-testid={`police-input-${claim.report_no}`}>
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          placeholder="Enter report #"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
          disabled={saving}
          className="flex-1 min-w-0 h-7 px-2 text-xs rounded border border-blue-300 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder:text-gray-400"
          data-testid={`police-input-field-${claim.report_no}`}
        />
        <button
          onClick={handleSave}
          disabled={saving || !value.trim()}
          className="p-1 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 transition-colors"
          data-testid={`police-input-save-${claim.report_no}`}
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          data-testid={`police-input-cancel-${claim.report_no}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function TodoDropdown() {
  const [items, setItems] = useState({ overdue: [], due_today: [], police_report_pending: [] });
  const [open, setOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const dropdownRef = useRef(null);
  const closeTimerRef = useRef(null);
  const navigate = useNavigate();

  const startCloseTimer = useCallback(() => {
    closeTimerRef.current = setTimeout(() => { setOpen(false); setEditingReport(null); }, 1500);
  }, []);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);

  useEffect(() => { return () => cancelCloseTimer(); }, [cancelCloseTimer]);

  const fetchOverdue = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/claims/overdue`);
      setItems({
        overdue: data.overdue || [],
        due_today: data.due_today || [],
        police_report_pending: data.police_report_pending || [],
      });
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchOverdue();
    const interval = setInterval(fetchOverdue, 60000);
    return () => clearInterval(interval);
  }, [fetchOverdue]);

  useEffect(() => {
    const handler = () => fetchOverdue();
    window.addEventListener('todo-refresh', handler);
    return () => window.removeEventListener('todo-refresh', handler);
  }, [fetchOverdue]);

  // Reset editing when dropdown closes
  useEffect(() => {
    if (!open) setEditingReport(null);
  }, [open]);

  const total = items.overdue.length + items.due_today.length + items.police_report_pending.length;
  if (total === 0) return null;

  const handleClick = (claim) => {
    setOpen(false);
    navigate('/companies', {
      state: { selectedCompany: claim.company_name, openClaim: claim.report_no },
    });
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="todo-dropdown-container">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
        data-testid="todo-dropdown-btn"
      >
        <ClipboardList className="w-4 h-4" />
        <span className="hidden sm:inline">To Do</span>
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
          {total > 99 ? '99+' : total}
        </span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-80 sm:w-96 max-h-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-50 flex flex-col overflow-hidden"
          data-testid="todo-dropdown-list"
          onMouseLeave={startCloseTimer}
          onMouseEnter={cancelCloseTimer}
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">To Do — {total} claim{total !== 1 ? 's' : ''} need attention</span>
          </div>

          <div className="overflow-y-auto flex-1">
            {/* Overdue section */}
            {items.overdue.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1">
                  <span className="text-[11px] font-semibold text-red-600 uppercase tracking-wider">Overdue</span>
                </div>
                {items.overdue.map((c) => (
                  <button
                    key={c.report_no}
                    onClick={() => handleClick(c)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-red-50/50 transition-colors border-b border-gray-50"
                    data-testid={`todo-item-${c.report_no}`}
                  >
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate">{c.report_no}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 whitespace-nowrap">{c.badge}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{c.company_name}</p>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Due Today section */}
            {items.due_today.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1">
                  <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-wider">Due Today</span>
                </div>
                {items.due_today.map((c) => (
                  <button
                    key={c.report_no}
                    onClick={() => handleClick(c)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-amber-50/50 transition-colors border-b border-gray-50"
                    data-testid={`todo-item-${c.report_no}`}
                  >
                    <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate">{c.report_no}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">{c.badge}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{c.company_name}</p>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Police Report Pending section */}
            {items.police_report_pending.length > 0 && (
              <>
                <div className="px-4 pt-3 pb-1">
                  <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider">Police Report Pending</span>
                </div>
                {items.police_report_pending.map((c) => (
                  <div key={`police-${c.report_no}`}>
                    <div
                      className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-blue-50/50 transition-colors border-b border-gray-50"
                      data-testid={`todo-police-${c.report_no}`}
                    >
                      <ShieldAlert className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleClick(c)} className="text-sm font-semibold text-gray-900 truncate hover:underline">
                            {c.report_no}
                          </button>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">{c.badge}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-xs text-gray-500 truncate">{c.company_name}</p>
                          {editingReport !== c.report_no && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingReport(c.report_no); }}
                              className="text-[10px] font-medium text-blue-600 hover:text-blue-800 hover:underline shrink-0 ml-2"
                              data-testid={`police-enter-btn-${c.report_no}`}
                            >
                              Enter Report #
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {editingReport === c.report_no && (
                      <PoliceReportInput
                        claim={c}
                        onSaved={() => { setEditingReport(null); fetchOverdue(); }}
                        onCancel={() => setEditingReport(null)}
                      />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
